import {
  Spin,
  Empty,
  Typography,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
  Card,
  Row,
  Col,
  Divider,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAxiosPost from '../../hooks/useAxiosPost';
import useMultiNotification from '../../hooks/useNotification';
import { formatDate } from '../../helpers/date';
import { getUserType } from '../../helpers/userDetails';
import '../Items/Table/index.css';

const { Option } = Select;

interface BatchTableDataType {
  key: number;
  intPkBatchID: number;
  intPkCompanyID: number;
  batchName: string;
  intPkPackageID: number;
  packageName: string;
  batchLength: number;
  batchProcessStatus: 'PENDING' | 'PROCESSED';
  speedUp: number;
  speedDown: number;
  speedUnit: 'K' | 'M' | 'G';
  maxDataVolumeLimit: number | null;
  maxDataVolumeUnit: 'K' | 'M' | 'G' | null;
  voucherCharactersLimit: number;
  voucherNumberType: 'NUMERIC' | 'ALPHANUMERIC';
  packageDurationEnum: 'HOURS' | 'DAYS';
  packageDurationDays: number;
  packageDurationHours: number;
  dateCreated?: string;
  enable?: number;
  totalVouchers?: number;
  usedVouchers?: number;
  remainingVouchers?: number;
}

interface PackageOption {
  intPkPackageID: number;
  packageName: string;
  speedUp: number;
  speedDown: number;
  speedUnit: 'K' | 'M' | 'G';
  packageDurationEnum: 'HOURS' | 'DAYS';
  packageDurationDays: number;
  packageDurationHours: number;
  maxDataVolumeLimit?: number | null;
  maxDataVolumeUnit?: 'K' | 'M' | 'G' | null;
}

const BatchesComponent = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | undefined>(undefined);
  const [packageDurationEnum, setPackageDurationEnum] = useState<'HOURS' | 'DAYS' | undefined>(undefined);

  const [postApiData, apiResponse, apiError, apiLoading, apiMessage, apiErrorCount] =
    useAxiosPost<ResponseProp>('batches/all');

  const [
    createBatchData,
    createBatchResponse,
    createBatchError,
    createBatchLoading,
    createBatchMessage,
    createBatchErrorCount,
  ] = useAxiosPost<ResponseProp>('batches/create');

  const [fetchPackagesData, packagesResponse, packagesError, packagesLoading, packagesMessage, packagesErrorCount] =
    useAxiosPost<ResponseProp>('packages/all');

  const [allBatches, setAllBatches] = useState<BatchTableDataType[]>([]);
  const [packages, setPackages] = useState<PackageOption[]>([]);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 25,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} batches`,
  });
  const postApiDataRef = useRef(postApiData);
  const hasProcessedCreateSuccess = useRef(false);
  const lastProcessedErrorCount = useRef({ api: 0, create: 0, packages: 0 });

  // Keep ref updated
  useEffect(() => {
    postApiDataRef.current = postApiData;
  }, [postApiData]);

  useEffect(() => {
    postApiDataRef.current({});
    fetchPackagesData({});
  }, []);

  useEffect(() => {
    if (apiResponse !== null) {
      const responseData: any = apiResponse.responseData;
      if (Array.isArray(responseData)) {
        const batchesWithKeys = responseData.map((batch: any) => ({
          ...batch,
          key: batch.intPkBatchID,
        }));
        setAllBatches(batchesWithKeys);
      } else {
        setAllBatches([]);
      }
    }
  }, [apiResponse]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: allBatches.length,
      current: 1,
    }));
  }, [allBatches]);

  useEffect(() => {
    if (packagesResponse !== null) {
      const responseData: any = packagesResponse.responseData;
      if (Array.isArray(responseData)) {
        setPackages(responseData);
      } else {
        setPackages([]);
      }
    }
  }, [packagesResponse]);

  useEffect(() => {
    if (apiError !== null && apiErrorCount !== 0 && lastProcessedErrorCount.current.api !== apiErrorCount) {
      lastProcessedErrorCount.current.api = apiErrorCount;
      openNotification('error', 'Failed to Load Batches', apiError);
    }
  }, [apiError, apiErrorCount, openNotification]);

  // Handle create batch success
  useEffect(() => {
    if (createBatchResponse !== null && !hasProcessedCreateSuccess.current) {
      hasProcessedCreateSuccess.current = true;
      openNotification('success', 'Batch Created', 'Batch created successfully');
      setIsModalOpen(false);
      form.resetFields();
      // Set default values after reset
      form.setFieldsValue({
        voucherCharactersLimit: 8,
        voucherNumberType: 'NUMERIC',
      });
      setSelectedPackageId(undefined);
      setPackageDurationEnum(undefined);
      // Refresh batches list
      setTimeout(() => {
        postApiDataRef.current({});
      }, 100);
    }
  }, [createBatchResponse]);

  // Reset the flag when modal opens or createBatchResponse becomes null
  useEffect(() => {
    if (createBatchResponse === null) {
      hasProcessedCreateSuccess.current = false;
    }
  }, [createBatchResponse]);

  // Handle create batch error
  useEffect(() => {
    if (
      createBatchError !== null &&
      createBatchErrorCount !== 0 &&
      lastProcessedErrorCount.current.create !== createBatchErrorCount
    ) {
      lastProcessedErrorCount.current.create = createBatchErrorCount;
      openNotification('error', 'Failed to Create Batch', createBatchError);
    }
  }, [createBatchError, createBatchErrorCount, openNotification]);

  const showModal = () => {
    setIsModalOpen(true);
    // Set default values when modal opens
    form.setFieldsValue({
      voucherCharactersLimit: 8,
      voucherNumberType: 'NUMERIC',
    });
  };

  const handleCancel = () => {
    if (!createBatchLoading) {
      setIsModalOpen(false);
      form.resetFields();
      // Set default values after reset
      form.setFieldsValue({
        voucherCharactersLimit: 8,
        voucherNumberType: 'NUMERIC',
      });
      setSelectedPackageId(undefined);
      setPackageDurationEnum(undefined);
    }
  };

  const handleModalClose = () => {
    if (!createBatchLoading) {
      handleCancel();
    }
  };

  const handleCreate = (values: any) => {
    const { intPkPackageID, batchName, batchLength, voucherCharactersLimit, voucherNumberType } =
      values;

    createBatchData({
      intPkPackageID,
      batchName: batchName.trim(),
      batchLength,
      voucherCharactersLimit,
      voucherNumberType,
    });
  };

  const handlePackageSelect = (packageId: number) => {
    setSelectedPackageId(packageId);
    const selectedPackage = packages.find((pkg) => pkg.intPkPackageID === packageId);
    if (selectedPackage) {
      setPackageDurationEnum(selectedPackage.packageDurationEnum);
    }
  };

  const getSelectedPackage = () => {
    if (!selectedPackageId) return null;
    return packages.find((pkg) => pkg.intPkPackageID === selectedPackageId);
  };

  const formatDuration = (packageData: PackageOption) => {
    if (packageData.packageDurationEnum === 'HOURS') {
      return `${packageData.packageDurationHours} Hour${packageData.packageDurationHours !== 1 ? 's' : ''}`;
    } else {
      return `${packageData.packageDurationDays} Day${packageData.packageDurationDays !== 1 ? 's' : ''}`;
    }
  };

  const getSpeedDisplay = (speed: number, unit: string) => {
    return `${speed} ${unit}bps`;
  };

  const getPackageDataVolumeDisplay = (pkg: PackageOption | null) => {
    if (!pkg || pkg.maxDataVolumeLimit == null || !pkg.maxDataVolumeUnit) return 'No limit';
    return `${pkg.maxDataVolumeLimit} ${pkg.maxDataVolumeUnit}B`;
  };

  const getDataVolumeDisplay = (batch: BatchTableDataType) => {
    if (batch.maxDataVolumeLimit != null && batch.maxDataVolumeUnit) {
      return `${batch.maxDataVolumeLimit} ${batch.maxDataVolumeUnit}B`;
    }
    return 'Unlimited';
  };

  const getBatchDurationDisplay = (batch: BatchTableDataType) => {
    if (batch.packageDurationEnum === 'HOURS') {
      return `${batch.packageDurationHours} Hour${batch.packageDurationHours !== 1 ? 's' : ''}`;
    }
    if (batch.packageDurationEnum === 'DAYS') {
      return `${batch.packageDurationDays} Day${batch.packageDurationDays !== 1 ? 's' : ''}`;
    }
    return '-';
  };

  const handleRowClick = (record: BatchTableDataType) => {
    navigate(`/batch-details/${record.intPkBatchID}`);
  };

  const handleRefresh = () => {
    postApiData({});
  };

  const handleTableChange = (paginationConfig: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || prev.pageSize,
    }));
  };

  const columns: ColumnsType<BatchTableDataType> = [
    {
      title: 'Sr#',
      key: 'serial',
      align: 'center',
      width: 80,
      render: (_: any, __: BatchTableDataType, index: number) => {
        const current = pagination.current || 1;
        const pageSize = pagination.pageSize || 25;
        return (current - 1) * pageSize + index + 1;
      },
    },
    {
      title: 'Batch Name',
      dataIndex: 'batchName',
      key: 'batchName',
      sorter: (a, b) => a.batchName.localeCompare(b.batchName),
      render: (text: string, record: BatchTableDataType) => (
        <Typography.Text
          strong
          style={{
            textDecoration: record.enable === 0 ? 'line-through' : 'none',
            color: record.enable === 0 ? '#ff4d4f' : undefined,
          }}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      title: 'Package',
      dataIndex: 'packageName',
      key: 'packageName',
      render: (text: string, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Speed',
      key: 'speed',
      render: (_, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {getSpeedDisplay(record.speedDown, record.speedUnit)}
        </span>
      ),
    },
    {
      title: 'Data Volume',
      key: 'dataVolume',
      render: (_, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {getDataVolumeDisplay(record)}
        </span>
      ),
    },
    {
      title: 'Batch Duration',
      key: 'batchDuration',
      render: (_, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {getBatchDurationDisplay(record)}
        </span>
      ),
    },
    {
      title: 'Total Vouchers',
      dataIndex: 'batchLength',
      key: 'batchLength',
      align: 'center',
      sorter: (a, b) => (a.batchLength || 0) - (b.batchLength || 0),
      render: (count: number, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {count}
        </span>
      ),
    },
    {
      title: 'Used Vouchers',
      dataIndex: 'usedVouchers',
      key: 'usedVouchers',
      sorter: (a, b) => (a.usedVouchers || 0) - (b.usedVouchers || 0),
      render: (count: number, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {count ?? 0}
        </span>
      ),
      align: 'center',
    },
    {
      title: 'Remaining',
      key: 'remaining',
      sorter: (a, b) =>
        (a.remainingVouchers ?? a.batchLength ?? 0) - (b.remainingVouchers ?? b.batchLength ?? 0),
      render: (_, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {record.remainingVouchers ?? record.batchLength}
        </span>
      ),
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'batchProcessStatus',
      key: 'batchProcessStatus',
      sorter: (a, b) => {
        const getStatusRank = (record: BatchTableDataType) => {
          if (record.enable === 0) return 0;
          if (record.batchProcessStatus === 'PENDING') return 1;
          if (record.batchProcessStatus === 'PROCESSED') return 2;
          return 3;
        };
        return getStatusRank(a) - getStatusRank(b);
      },
      render: (status: string, record: BatchTableDataType) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {record.enable === 0 ? (
            <Tag color="red">Disabled</Tag>
          ) : (
            <>
              {status === 'PENDING' && <Tag color="orange">PENDING</Tag>}
              {status === 'PROCESSED' && <Tag color="success">Active</Tag>}
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Date Created',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      sorter: (a, b) => {
        const aTime = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
        const bTime = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
        return aTime - bTime;
      },
      defaultSortOrder: 'descend',
      render: (date: string, record: BatchTableDataType) => (
        <span style={{ color: record.enable === 0 ? '#ff4d4f' : undefined }}>
          {date ? formatDate(date) : '-'}
        </span>
      ),
    },
  ];

  const userRole = getUserType();
  const canCreateBatch = userRole === 'ADMIN';

  return (
    <>
      {contextHolder}
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="large" loading={apiLoading}>
          Refresh
        </Button>
        {canCreateBatch && (
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal} size="large">
            Create Batch
          </Button>
        )}
      </div>

      <Modal
        title="Create New Batch"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={700}
        maskClosable={!createBatchLoading}
        closable={!createBatchLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate} requiredMark={false}>
          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Package</span>}
            name="intPkPackageID"
            rules={[
              {
                required: true,
                message: 'Please select a package',
              },
            ]}
          >
            <Select
              placeholder="Select a package"
              disabled={createBatchLoading || packagesLoading}
              loading={packagesLoading}
              onChange={handlePackageSelect}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const label = String(option?.children ?? '').toLowerCase();
                return label.includes(input.toLowerCase());
              }}
            >
              {packages.map((pkg) => (
                <Option key={pkg.intPkPackageID} value={pkg.intPkPackageID}>
                  {pkg.packageName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {getSelectedPackage() && (
            <Card
              style={{
                marginBottom: 24,
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                border: 'none',
                borderRadius: 8,
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <Typography.Title level={5} style={{ margin: 0, marginBottom: 16, color: '#262626' }}>
                Package Details
              </Typography.Title>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Download Speed
                    </Typography.Text>
                    <Typography.Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                      {getSelectedPackage()?.speedDown} {getSelectedPackage()?.speedUnit}bps
                    </Typography.Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Upload Speed
                    </Typography.Text>
                    <Typography.Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                      {getSelectedPackage()?.speedUp} {getSelectedPackage()?.speedUnit}bps
                    </Typography.Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Duration
                    </Typography.Text>
                    <Typography.Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                      {getSelectedPackage() && formatDuration(getSelectedPackage()!)}
                    </Typography.Text>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Data Limit (from package)
                    </Typography.Text>
                    <Typography.Text strong style={{ fontSize: 16, color: '#722ed1' }}>
                      {getPackageDataVolumeDisplay(getSelectedPackage() ?? null)}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Batch Name</span>}
            name="batchName"
            rules={[
              {
                required: true,
                message: 'Please enter batch name',
              },
              {
                min: 1,
                message: 'Batch name cannot be empty',
              },
              {
                max: 200,
                message: 'Batch name must be less than 200 characters',
              },
              {
                whitespace: true,
                message: 'Batch name cannot be only spaces',
              },
            ]}
          >
            <Input placeholder="Enter batch name" disabled={createBatchLoading} maxLength={200} showCount />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Vouchers Qty (Max: 1000)</span>}
            name="batchLength"
            rules={[
              {
                required: true,
                message: 'Please enter vouchers quantity',
              },
              {
                type: 'number',
                min: 1,
                max: 1000,
                message: 'Vouchers quantity must be between 1 and 1000',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter vouchers quantity"
              disabled={createBatchLoading}
              style={{ width: '100%' }}
              min={1}
              max={1000}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Voucher Characters Limit (8 - 12)</span>}
            name="voucherCharactersLimit"
            initialValue={8}
            rules={[
              {
                required: true,
                message: 'Please enter voucher characters limit',
              },
              {
                type: 'number',
                min: 8,
                max: 12,
                message: 'Voucher characters limit must be between 8 and 12',
              },
            ]}
          >
            <InputNumber
              placeholder="Enter voucher characters limit (8-12)"
              disabled={createBatchLoading}
              style={{ width: '100%' }}
              min={8}
              max={12}
            />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontWeight: 500 }}>Voucher value Type</span>}
            name="voucherNumberType"
            initialValue="NUMERIC"
            rules={[
              {
                required: true,
                message: 'Please select voucher value type',
              },
            ]}
          >
            <Select placeholder="Select voucher value type" disabled={createBatchLoading}>
              <Option value="NUMERIC">NUMERIC</Option>
              <Option value="ALPHANUMERIC">ALPHANUMERIC</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: '8px' }} disabled={createBatchLoading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={createBatchLoading} disabled={createBatchLoading}>
              {createBatchLoading ? 'Creating...' : 'Create Batch'}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <Typography style={{ marginTop: '16px', display: 'block' }}>Loading batches...</Typography>
        </div>
      ) : apiError && !apiLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty
            description={<Typography.Text type="danger">{apiError || 'Failed to load batches'}</Typography.Text>}
          />
        </div>
      ) : allBatches.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="No batches found. Create your first batch!" />
        </div>
      ) : (
        <div className="table-container-custom">
          <Table
            className="antd-table-striped batches-table-clickable"
            columns={columns}
            dataSource={allBatches}
            pagination={pagination}
            loading={apiLoading}
            onChange={(paginationConfig) => handleTableChange(paginationConfig as TablePaginationConfig)}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              className: record.enable === 0 ? 'batch-disabled' : '',
              style: { 
                cursor: 'pointer',
                backgroundColor: record.enable === 0 ? '#fff1f0' : undefined,
                opacity: record.enable === 0 ? 0.8 : 1,
              },
            })}
          />
        </div>
      )}
    </>
  );
};
export default BatchesComponent;
